import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { apiUrl } from "utils/apiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";
import {
  fxproPresetsForPlatform,
  isFxProServerName,
  presetsForPlatform,
} from "constants/mtKnownServers";

const selectClassDefault =
  "h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-navy-700 outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white";
const selectClassCompact =
  "h-9 w-full min-w-[160px] shrink-0 rounded-lg border border-gray-200 bg-white px-2 text-xs leading-none text-navy-700 outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white";

function sortNames(names) {
  return [...names].sort((a, b) =>
    a.localeCompare(b, "en", { sensitivity: "base" }),
  );
}

/**
 * Serveur broker MT4 / MT5 : menu déroulant filtrable (recherche + liste scrollable),
 * inspiré du sélecteur FxPro mobile. Données : presets par plateforme + GET /api/broker-servers.
 */
export default function BrokerServerField({
  platform,
  value,
  onChange,
  variant = "default",
  /** Path 1 Broker partner : menu limité aux serveurs FxPro (MT4 + MT5). */
  fxproOnly = false,
}) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState("");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [customMode, setCustomMode] = useState(false);
  const [menuRect, setMenuRect] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const reactId = useId().replace(/:/g, "");
  const listboxId = `as-broker-lb-${reactId}`;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setLoadError("");
      setNotice("");
      try {
        const res = await fetch(
          apiUrl(
            `/api/broker-servers?platform=${encodeURIComponent(platform)}`,
          ),
        );
        const ct = res.headers.get("content-type") || "";
        if (!ct.includes("application/json")) {
          throw new Error(
            "Service temporarily unavailable. Please refresh the page in a moment.",
          );
        }
        const data = await res.json();
        if (!data.ok) {
          throw new Error(
            clientSafeMessage(data.message, "Could not load server list."),
          );
        }
        if (!cancelled) {
          setGroups(Array.isArray(data.groups) ? data.groups : []);
          setNotice(typeof data.notice === "string" ? data.notice : "");
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError(clientSafeMessage(e?.message, "Network error."));
          setGroups([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [platform]);

  const allNames = useMemo(() => {
    const presetList = fxproOnly
      ? fxproPresetsForPlatform(platform)
      : presetsForPlatform(platform);
    const set = new Set(presetList);
    for (const g of groups) {
      for (const row of g.servers || []) {
        const n = String(row?.name || "").trim();
        if (!n) continue;
        if (fxproOnly && !isFxProServerName(n, platform)) continue;
        set.add(n);
      }
    }
    if (String(value || "").trim()) {
      const v = String(value).trim();
      if (!fxproOnly || isFxProServerName(v, platform)) set.add(v);
    }
    set.delete("");
    return sortNames([...set]);
  }, [groups, platform, value, fxproOnly]);

  const filtered = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return allNames;
    return allNames.filter((n) => n.toLowerCase().includes(q));
  }, [allNames, query]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuRect(null);
      return undefined;
    }
    const el = triggerRef.current;
    if (!el) return undefined;
    function measure() {
      const r = el.getBoundingClientRect();
      setMenuRect({
        top: r.bottom + 4,
        left: r.left,
        width: Math.max(r.width, 200),
      });
    }
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function onDocMouseDown(e) {
      const t = e.target;
      if (
        rootRef.current?.contains(t) ||
        panelRef.current?.contains(t)
      ) {
        return;
      }
      setOpen(false);
      setQuery("");
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  const selCls = variant === "compact" ? selectClassCompact : selectClassDefault;
  const wrapCls =
    variant === "compact"
      ? "flex min-w-[160px] shrink-0 flex-col gap-1"
      : "space-y-1.5";

  const apiCount = useMemo(() => {
    let n = 0;
    for (const g of groups) {
      n += (g.servers || []).filter((row) => String(row?.name || "").trim())
        .length;
    }
    return n;
  }, [groups]);

  const labelPlatform =
    String(platform || "").toUpperCase() === "MT5" ? "MetaTrader 5" : "MetaTrader 4";

  function pickServer(name) {
    onChange(name);
    setOpen(false);
    setQuery("");
    setCustomMode(false);
  }

  return (
    <div ref={rootRef} className={`relative ${wrapCls}`}>
      <label className="sr-only" htmlFor={`${listboxId}-trigger`}>
        Broker server ({labelPlatform})
      </label>

      {!customMode ? (
        <>
          <button
            ref={triggerRef}
            id={`${listboxId}-trigger`}
            type="button"
            disabled={loading}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={open ? listboxId : undefined}
            onClick={() => {
              setOpen((o) => !o);
              if (!open) setQuery("");
            }}
            className={`${selCls} flex w-full items-center justify-between gap-2 text-left ${
              loading ? "cursor-wait opacity-70" : "cursor-pointer hover:border-brand-300"
            }`}
          >
            <span className={value ? "truncate" : "truncate text-gray-500 dark:text-gray-400"}>
              {loading
                ? "Loading servers…"
                : value || "— Select a server —"}
            </span>
            <span className="shrink-0 text-gray-400" aria-hidden>
              {open ? "▲" : "▼"}
            </span>
          </button>

          {open && menuRect
            ? createPortal(
                <div
                  ref={panelRef}
                  style={{
                    position: "fixed",
                    top: menuRect.top,
                    left: menuRect.left,
                    width: menuRect.width,
                    zIndex: 9999,
                  }}
                  className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-white/15 dark:bg-navy-900"
                  role="presentation"
                >
                  <div className="border-b border-gray-100 p-2 dark:border-white/10">
                    <input
                      type="search"
                      autoComplete="off"
                      spellCheck={false}
                      placeholder={`Search ${labelPlatform} server…`}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      className="h-9 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm text-navy-700 outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-800 dark:text-white"
                      aria-label="Filter server list"
                    />
                  </div>
                  <ul
                    id={listboxId}
                    role="listbox"
                    className="max-h-52 overflow-y-auto py-1 text-sm"
                  >
                    {filtered.length === 0 ? (
                      <li className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                        No match. Try another search or use a custom server name.
                      </li>
                    ) : (
                      filtered.map((name) => (
                        <li key={name} role="presentation">
                          <button
                            type="button"
                            role="option"
                            aria-selected={value === name}
                            onClick={() => pickServer(name)}
                            className={`flex w-full items-start gap-2 px-3 py-2 text-left text-navy-800 hover:bg-brand-50 dark:text-white dark:hover:bg-white/10 ${
                              value === name
                                ? "bg-brand-50/80 font-semibold dark:bg-white/10"
                                : ""
                            }`}
                          >
                            <span className="min-w-0 flex-1 truncate">{name}</span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                  <div className="border-t border-gray-100 p-2 dark:border-white/10">
                    <button
                      type="button"
                      onClick={() => {
                        setCustomMode(true);
                        setOpen(false);
                        setQuery("");
                      }}
                      className="w-full rounded-lg py-1.5 text-center text-xs font-semibold text-brand-600 hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-white/10"
                    >
                      Other — type server manually
                    </button>
                  </div>
                </div>,
                document.body,
              )
            : null}
        </>
      ) : (
        <div className="space-y-1.5">
          <input
            className={selCls}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Exact server name from MetaTrader"
            aria-label="Broker server (custom)"
            autoComplete="off"
            spellCheck={false}
          />
          <button
            type="button"
            onClick={() => setCustomMode(false)}
            className="text-xs font-semibold text-brand-600 hover:underline dark:text-brand-300"
          >
            ← Back to server list
          </button>
        </div>
      )}

      {loadError ? (
        <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
          {loadError}{" "}
          <span className="font-normal text-gray-600 dark:text-gray-400">
            ({allNames.length} preset names shown until the list loads.)
          </span>
        </p>
      ) : null}
      {!loadError && !loading && variant === "default" && !customMode ? (
        <p className="text-[11px] leading-snug text-gray-500 dark:text-gray-400">
          {allNames.length} servers (FxPro presets and synced names). Search like in the mobile app,
          or pick from the list.
          {apiCount > 0 ? ` ${apiCount} more from sync.` : ""}
        </p>
      ) : null}
      {(() => {
        const safeNotice =
          notice && variant === "default" && !loadError
            ? clientSafeMessage(notice, "")
            : "";
        return safeNotice ? (
          <p className="text-[11px] leading-snug text-gray-500 dark:text-gray-400">
            {safeNotice}
          </p>
        ) : null;
      })()}
    </div>
  );
}
